import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRememberFilters } from "@/hooks/useRememberFilters";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChipFilter, ChipFilterOption } from "@/components/ui/chip-filter";
import { AddFilterMenu, FilterCategory } from "@/components/ui/add-filter-menu";

import { Plus, Pencil, UserCircle, Mail, MessageSquare, Trash2, Clock, MapPin, Shield } from "lucide-react";
import { toast } from "sonner";
import { Driver, DriverCertification, DriverShift, Location, Flight } from "@/types/tms";

import { FlightMiniView } from "@/components/drivers/FlightMiniView";
import { useCRUDMutations } from "@/hooks/useCRUDMutations";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { SortableHeader } from "@/components/ui/sortable-header";
import { useTableSort } from "@/hooks/useTableSort";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { DriverCard } from "@/components/drivers/DriverCard";

const CERTIFICATIONS: DriverCertification[] = ["VO", "NVO", "Manual"];
const SHIFTS: DriverShift[] = ["AM", "PM"];

type DriverFormData = {
  name: string;
  email: string;
  slack_handle: string;
  manager: string;
  home_base_id: string;
  flight_id: string;
  certification: DriverCertification | "";
  shift: DriverShift | "";
};

const transformDriverData = (data: DriverFormData) => ({
  ...data,
  certification: data.certification || null,
  shift: data.shift || null,
  home_base_id: data.home_base_id || null,
  flight_id: data.flight_id || null,
  manager: data.manager || null,
});

export default function Drivers() {
  const queryClient = useQueryClient();
  useRememberFilters();

  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const [shiftFilter, setShiftFilter] = useUrlArrayFilterState("shift");
  const [homeBaseFilter, setHomeBaseFilter] = useUrlArrayFilterState("homeBase");
  const [managerFilter, setManagerFilter] = useUrlArrayFilterState("manager");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [formData, setFormData] = useState<DriverFormData>({
    name: "", email: "", slack_handle: "", manager: "",
    home_base_id: "", flight_id: "", certification: "", shift: "",
  });

  const { data: drivers, isLoading } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select(`*, home_base:locations(*), flight:flights(*)`)
        .order("name");
      if (error) throw error;
      return data as (Driver & { home_base: Location; flight: Flight | null })[];
    },
  });

  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      return data as Location[];
    },
  });

  const { data: flights } = useQuery({
    queryKey: ["flights"],
    queryFn: async () => {
      const { data, error } = await supabase.from("flights").select("*");
      if (error) throw error;
      return (data as Flight[]).sort((a, b) => {
        const numA = parseInt(a.name.replace(/\D/g, "")) || 0;
        const numB = parseInt(b.name.replace(/\D/g, "")) || 0;
        return numA - numB;
      });
    },
  });

  const uniqueManagers = Array.from(
    new Set(drivers?.map((d) => d.manager).filter(Boolean) as string[])
  ).sort();

  const filteredDrivers = drivers?.filter((d) => {
    const matchesSearch =
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.manager?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesShift = shiftFilter.includes("all") || shiftFilter.includes(d.shift ?? "");
    const matchesHomeBase = homeBaseFilter.includes("all") || homeBaseFilter.includes(d.home_base_id ?? "");
    const matchesManager = managerFilter.includes("all") || managerFilter.includes(d.manager ?? "");
    return matchesSearch && matchesShift && matchesHomeBase && matchesManager;
  });
  const hasActiveFilter =
    !!searchQuery.trim() ||
    !shiftFilter.includes("all") ||
    !homeBaseFilter.includes("all") ||
    !managerFilter.includes("all");

  type DriverWithRelations = Driver & { home_base: Location; flight: Flight | null };
  const DRIVER_ACCESSORS = {
    name: (d: DriverWithRelations) => d.name,
    certification: (d: DriverWithRelations) => d.certification,
    shift: (d: DriverWithRelations) => d.shift,
    home_base: (d: DriverWithRelations) => d.home_base?.short_code,
    flight: (d: DriverWithRelations) => d.flight?.name,
  };
  const { sorted: sortedDrivers, sort, toggle: toggleSort } = useTableSort(filteredDrivers as DriverWithRelations[] | undefined, DRIVER_ACCESSORS);

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(sortedDrivers);


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedDrivers,
  } = useTablePagination(sortedDrivers as DriverWithRelations[] | undefined);

  const driversByManager = pagedDrivers.reduce((acc, driver) => {
    const manager = driver.manager || "Unassigned";
    if (!acc[manager]) acc[manager] = [];
    acc[manager].push(driver);
    return acc;
  }, {} as Record<string, DriverWithRelations[]>);

  const sortedManagers = Object.keys(driversByManager || {}).sort((a, b) => {
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });

  const resetForm = () => {
    setFormData({ name: "", email: "", slack_handle: "", manager: "", home_base_id: "", flight_id: "", certification: "", shift: "" });
    setEditingDriver(null);
    setIsDialogOpen(false);
  };

  /* BETA: addFlightMutation removed — Flights tab hidden */

  const createMutation = useMutation({
    mutationFn: async (data: DriverFormData) => {
      const { error } = await supabase.from("drivers").insert(transformDriverData(data));
      if (error) throw error;
      if (data.email) {
        await supabase.rpc("assign_driver_role_by_email", { _email: data.email });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Driver created successfully");
      resetForm();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const { updateMutation: baseUpdateMutation } = useCRUDMutations<DriverFormData>({
    tableName: "drivers",
    queryKey: "drivers",
    entityLabel: "Driver",
    transformUpdate: transformDriverData,
    onUpdateSuccess: resetForm,
  });

  const updateMutation = {
    ...baseUpdateMutation,
    mutate: (args: { id: string; data: DriverFormData }) => {
      baseUpdateMutation.mutate(args, {
        onSuccess: async () => {
          // Sync driver role if email was set or changed
          if (args.data.email) {
            await supabase.rpc("assign_driver_role_by_email", { _email: args.data.email });
          }
        },
      });
    },
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: unassignError } = await supabase
        .from("orders")
        .update({ assigned_operator_id: null })
        .eq("assigned_operator_id", id);
      if (unassignError) throw unassignError;
      const { error } = await supabase.from("drivers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Driver deleted successfully");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Unassign all drivers from orders first
      for (const id of ids) {
        const { error } = await supabase
          .from("orders")
          .update({ assigned_operator_id: null })
          .eq("assigned_operator_id", id);
        if (error) throw error;
      }
      const { error } = await supabase.from("drivers").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`Deleted ${count} driver(s)`);
      clear();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      email: driver.email || "",
      slack_handle: driver.slack_handle || "",
      manager: driver.manager || "",
      home_base_id: driver.home_base_id || "",
      flight_id: driver.flight_id || "",
      certification: driver.certification || "",
      shift: driver.shift || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.email) {
      const existingDriver = drivers?.find(
        (d) => d.email?.toLowerCase() === formData.email.toLowerCase() && d.id !== editingDriver?.id
      );
      if (existingDriver) {
        toast.error(`A driver with email "${formData.email}" already exists (${existingDriver.name}).`);
        return;
      }
    }
    if (editingDriver) {
      updateMutation.mutate({ id: editingDriver.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <AppLayout
      title="Drivers"
      actions={
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> Add Driver</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingDriver ? "Edit Driver" : "Add Driver"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Smith" required />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com" />
                  </div>
                  <div>
                    <Label htmlFor="slack">Slack Handle</Label>
                    <Input id="slack" value={formData.slack_handle} onChange={(e) => setFormData({ ...formData, slack_handle: e.target.value })} placeholder="@john.smith" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="manager">Manager</Label>
                  <Input id="manager" value={formData.manager} onChange={(e) => setFormData({ ...formData, manager: e.target.value })} placeholder="Manager name" />
                </div>
                {/* BETA: Certification & Shift fields hidden */}
                <div>
                  <Label>Home Base</Label>
                  <Select value={formData.home_base_id} onValueChange={(value) => setFormData({ ...formData, home_base_id: value })}>
                    <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations?.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.short_code}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {/* BETA: Flight field hidden */}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{editingDriver ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
      }
    >
      <div className="animate-fade-in">

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
            <DriverFilters
              shiftFilter={shiftFilter}
              onShiftFilterChange={setShiftFilter}
              homeBaseFilter={homeBaseFilter}
              onHomeBaseFilterChange={setHomeBaseFilter}
              managerFilter={managerFilter}
              onManagerFilterChange={setManagerFilter}
              locations={locations}
              uniqueManagers={uniqueManagers}
            />
            <div className="ml-auto flex items-center gap-3">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {filteredDrivers?.length ?? 0} driver{(filteredDrivers?.length ?? 0) === 1 ? "" : "s"}</span>
              <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search drivers..." />
            </div>
          </div>

          {isLoading ? (
            <div className="rounded-lg border bg-card p-8 text-center">Loading...</div>
          ) : sortedManagers.length ? (
            <div>
            <BulkActionBar count={count} onClear={clear} entityLabel="driver">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
                onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                disabled={bulkDeleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </BulkActionBar>
            <div className="space-y-6">
              {sortedManagers.map((manager) => (
                <div key={manager} className="rounded-lg border bg-card overflow-hidden">
                  <div className="bg-muted/50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <UserCircle className="h-4 w-4 text-muted-foreground" />
                      {manager}
                      <span className="text-muted-foreground font-normal">({driversByManager?.[manager]?.length || 0} drivers)</span>
                    </h3>
                  </div>
                  <ResponsiveTableShell
                    cards={
                      <div className="flex flex-col gap-3 p-3">
                        {driversByManager?.[manager]?.map((driver) => (
                          <DriverCard
                            key={driver.id}
                            driver={driver}
                            isChecked={selectedIds.has(driver.id)}
                            onToggle={toggle}
                            onEdit={handleEdit}
                            onDelete={(id) => deleteMutation.mutate(id)}
                          />
                        ))}
                      </div>
                    }
                    table={
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Select all"
                            {...(someSelected ? { "data-state": "indeterminate" } : {})}
                          />
                        </TableHead>
                        <TableHead><SortableHeader label="Driver" sortKey="name" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                        <TableHead>Contact</TableHead>
                        {/* BETA: Flight, Certification, Shift columns hidden */}
                        <TableHead><SortableHeader label="Home Base" sortKey="home_base" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driversByManager?.[manager]?.map((driver) => (
                        <TableRow key={driver.id}>
                          <TableCell className="w-[40px]">
                            <Checkbox
                              checked={selectedIds.has(driver.id)}
                              onCheckedChange={() => toggle(driver.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <UserCircle className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{driver.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 text-sm">
                              {driver.email && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Mail className="h-3 w-3" />{driver.email}
                                </div>
                              )}
                              {driver.slack_handle && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <MessageSquare className="h-3 w-3" />{driver.slack_handle}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          {/* BETA: Flight, Certification, Shift cells hidden */}
                          <TableCell>{driver.home_base?.short_code || "—"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(driver)}><Pencil className="h-4 w-4" /></Button>
                              <ConfirmDeleteButton
                                onConfirm={() => deleteMutation.mutate(driver.id)}
                                entityName="driver"
                                entityLabel={driver.name}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                    }
                  />
                </div>
              ))}
            </div>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              className="rounded-b-lg border bg-card mt-4"
            />
            </div>
          ) : (
            <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
              {hasActiveFilter ? "No drivers match your filter criteria" : "No drivers found"}
            </div>
          )}
        </div>
      </div>

    </AppLayout>
  );
}

type DriverFilterKey = "shift" | "homeBase" | "manager";

const SHIFT_OPTIONS: ChipFilterOption[] = SHIFTS.map((s) => ({ value: s, label: s }));

function DriverFilters({
  shiftFilter,
  onShiftFilterChange,
  homeBaseFilter,
  onHomeBaseFilterChange,
  managerFilter,
  onManagerFilterChange,
  locations,
  uniqueManagers,
}: {
  shiftFilter: string[];
  onShiftFilterChange: (v: string[]) => void;
  homeBaseFilter: string[];
  onHomeBaseFilterChange: (v: string[]) => void;
  managerFilter: string[];
  onManagerFilterChange: (v: string[]) => void;
  locations: Location[] | undefined;
  uniqueManagers: string[];
}) {
  const [activeFilters, setActiveFilters] = useState<DriverFilterKey[]>(() => {
    const initial: DriverFilterKey[] = [];
    if (!shiftFilter.includes("all")) initial.push("shift");
    if (!homeBaseFilter.includes("all")) initial.push("homeBase");
    if (!managerFilter.includes("all")) initial.push("manager");
    return initial;
  });
  const [openFilterKey, setOpenFilterKey] = useState<DriverFilterKey | null>(null);
  const ignoreAutoCloseRef = useRef<DriverFilterKey | null>(null);

  const addFilter = useCallback((key: string) => {
    const fk = key as DriverFilterKey;
    if (activeFilters.includes(fk)) return;
    setActiveFilters((prev) => [...prev, fk]);
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, [activeFilters]);

  const removeFilter = useCallback((key: DriverFilterKey) => {
    setActiveFilters((prev) => prev.filter((k) => k !== key));
    setOpenFilterKey((prev) => (prev === key ? null : prev));
    if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
    if (key === "shift") onShiftFilterChange(["all"]);
    if (key === "homeBase") onHomeBaseFilterChange(["all"]);
    if (key === "manager") onManagerFilterChange(["all"]);
  }, [onShiftFilterChange, onHomeBaseFilterChange, onManagerFilterChange]);

  const getFilterValue = (key: DriverFilterKey): string[] => {
    if (key === "shift") return shiftFilter;
    if (key === "homeBase") return homeBaseFilter;
    if (key === "manager") return managerFilter;
    return ["all"];
  };

  const hasAnyChipFilter = activeFilters.length > 0;

  const resetAllFilters = () => {
    setActiveFilters([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onShiftFilterChange(["all"]);
    onHomeBaseFilterChange(["all"]);
    onManagerFilterChange(["all"]);
  };

  const locationOptions: ChipFilterOption[] = (locations ?? []).map((loc) => ({
    value: loc.id,
    label: loc.short_code,
  }));

  const managerOptions: ChipFilterOption[] = uniqueManagers.map((m) => ({
    value: m,
    label: m,
  }));

  const allCategories: (FilterCategory & { key: DriverFilterKey })[] = [
    { key: "shift", label: "Shift", icon: <Clock className="h-3.5 w-3.5" /> },
    { key: "homeBase", label: "Home Base", icon: <MapPin className="h-3.5 w-3.5" /> },
    { key: "manager", label: "Manager", icon: <UserCircle className="h-3.5 w-3.5" /> },
  ];

  const availableCategories = allCategories
    .filter((c) => !activeFilters.includes(c.key))
    .map(({ key, label, icon }) => ({ key, label, icon }));

  const optionsMap: Record<DriverFilterKey, { label: string; options: ChipFilterOption[]; value: string[]; onChange: (v: string | string[]) => void }> = {
    shift: { label: "Shift", options: SHIFT_OPTIONS, value: shiftFilter, onChange: (v) => onShiftFilterChange(Array.isArray(v) ? v : [v]) },
    homeBase: { label: "Home Base", options: locationOptions, value: homeBaseFilter, onChange: (v) => onHomeBaseFilterChange(Array.isArray(v) ? v : [v]) },
    manager: { label: "Manager", options: managerOptions, value: managerFilter, onChange: (v) => onManagerFilterChange(Array.isArray(v) ? v : [v]) },
  };

  const renderChip = (key: DriverFilterKey) => {
    const cfg = optionsMap[key];
    const categoryIcon = allCategories.find((c) => c.key === key)?.icon;
    return (
      <ChipFilter
        key={key}
        label={cfg.label}
        icon={categoryIcon}
        options={cfg.options}
        value={cfg.value}
        onChange={cfg.onChange}
        onRemove={() => removeFilter(key)}
        multiSelect
        open={openFilterKey === key}
        onOpenChange={(isOpen) => {
          if (!isOpen && ignoreAutoCloseRef.current === key) {
            ignoreAutoCloseRef.current = null;
            return;
          }
          if (!isOpen) {
            if (getFilterValue(key).includes("all")) {
              setActiveFilters((prev) => prev.filter((k) => k !== key));
            }
            setOpenFilterKey((prev) => (prev === key ? null : prev));
          } else {
            setOpenFilterKey(key);
          }
        }}
      />
    );
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {activeFilters.map((key) => renderChip(key))}
      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={hasAnyChipFilter}
      />
      {hasAnyChipFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={resetAllFilters}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}
